import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Badge } from '../types';

interface BadgePickerProps {
  badges: Badge[];
  selected: string; // badge ID or GUID
  onSelect: (badgeId: string) => void;
  placeholder?: string;
}

export function BadgePicker({ badges, selected, onSelect, placeholder }: BadgePickerProps) {
  const { t } = useTranslation('dashboard');
  const placeholderText = placeholder ?? t('awards.selectBadge', 'Select a badge...');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedBadge = useMemo(
    () => badges.find(b => b.id === selected || b.id.split('/').pop() === selected),
    [badges, selected]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return badges;
    const q = search.toLowerCase();
    return badges.filter(
      b => b.name.toLowerCase().includes(q) ||
           b.description?.toLowerCase().includes(q) ||
           b.issuer?.name?.toLowerCase().includes(q)
    );
  }, [badges, search]);

  function handleSelect(badge: Badge) {
    // Return the GUID — feeds into createAward body or path-param routes;
    // backend resolves to full URL (with legacy-domain fallback).
    onSelect(badge.id.split('/').pop() || badge.id);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="relative">
      {/* Trigger button showing selected badge or placeholder */}
      <button
        type="button"
        data-testid="open-badge-picker"
        data-track="Open badge picker"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:border-gray-300 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
      >
        {selectedBadge ? (
          <>
            <img
              src={selectedBadge.image}
              alt=""
              className="h-10 w-10 rounded-lg object-contain bg-gray-50 flex-shrink-0"
              onError={e => { (e.target as HTMLImageElement).src = '/assets/badge-templates/Template-000.png'; }}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 truncate">{selectedBadge.name}</p>
              <p className="text-xs text-gray-500 truncate">{selectedBadge.issuer?.name}</p>
            </div>
          </>
        ) : (
          <span className="text-gray-400">{placeholderText}</span>
        )}
        <svg className="h-4 w-4 text-gray-400 flex-shrink-0 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      </button>

      {/* Popup overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => { setOpen(false); setSearch(''); }} />
          <div className="relative z-10 w-full max-w-2xl max-h-[80vh] rounded-2xl bg-white shadow-xl flex flex-col overflow-hidden">
            {/* Header + search */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Select Badge</h3>
                <button onClick={() => { setOpen(false); setSearch(''); }} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('awards.badgePickerSearchPlaceholder', 'Search by name, description, or issuer...')}
                autoFocus
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>

            {/* Badge grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">
                  {badges.length === 0 ? 'No badges available. Create a badge first.' : 'No badges match your search.'}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {filtered.map(badge => {
                    const isSelected = badge.id === selected || badge.id.split('/').pop() === selected;
                    return (
                      <button
                        key={badge.id}
                        data-testid={`badge-card-${badge.id.split('/').pop() || badge.id}`}
                        data-track={`Pick badge: ${badge.name}`}
                        onClick={() => handleSelect(badge)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={badge.image}
                          alt=""
                          className="h-14 w-14 rounded-lg object-contain bg-gray-50 flex-shrink-0"
                          onError={e => { (e.target as HTMLImageElement).src = '/assets/badge-templates/Template-000.png'; }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-primary-700' : 'text-gray-900'}`}>
                            {badge.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {badge.issuer?.name}
                          </p>
                          <p className="text-xs text-gray-400 line-clamp-2 mt-1">
                            {badge.description}
                          </p>
                        </div>
                        {isSelected && (
                          <svg className="h-5 w-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
